import {
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type SerializableRecord } from '@post-office/serializable-record';

import { type EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import React from 'react';

import type { MessageTemplateContext } from './test-messages/jira-sidenav4-2p-app-recommendations-test';

// Type that represents the actual runtime structure after flattening
type FlattenedMessage<T extends SerializableRecord> = Omit<EligibleMessage<T>, 'context'> & T;

export type MarketplaceRecommendationMessage = FlattenedMessage<MessageTemplateContext>;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'jira-sidenav4-mpac-recommendations',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
};

const placement = {
	createFrontend: createPlacementFrontend(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;

// Headless placement component that requires children function for custom rendering
export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	{
		children: (props: {
			messages: Array<MarketplaceRecommendationMessage>;
			isLoading: boolean;
			error: Error | null | undefined;
		}) => React.ReactNode;
	}
> = createFrontend(
	() =>
		({
			children,
		}: {
			children: (props: {
				messages: Array<MarketplaceRecommendationMessage>;
				isLoading: boolean;
				error: Error | null | undefined;
			}) => React.ReactNode;
		}) => {
			const { data, error, isLoading } = usePlacementApi<{
				messages: Array<MarketplaceRecommendationMessage>;
			}>('/');

			// Call the required children function with all the state data
			return (
				<>
					{children({
						messages: data?.messages || [],
						isLoading,
						error,
					})}
				</>
			);
		},
);

export type PlacementFrontendProps = {
	children: (props: {
		messages: Array<MarketplaceRecommendationMessage>;
		isLoading: boolean;
		error: Error | null | undefined;
	}) => React.ReactNode;
};
